'use client';

import { useAuth } from '@clerk/clerk-react';
import { useQuery } from '@tanstack/react-query';

import { apiClient } from '../api/client';
import { components } from '../api/generated/schema';

type CITokenWire = components['schemas']['CIToken'];

export interface CIToken {
  id: string;
  name: string;
  tokenPrefix: string;
  createdAt: string;
  lastUsedAt: string | null;
  expiresAt: string | null;
  revokedAt: string | null;
}

function transformCIToken(t: CITokenWire): CIToken {
  return {
    id: t.id,
    name: t.name,
    tokenPrefix: t.tokenPrefix,
    createdAt: t.createdAt,
    lastUsedAt: t.lastUsedAt ?? null,
    expiresAt: t.expiresAt ?? null,
    revokedAt: t.revokedAt ?? null,
  };
}

/**
 * Hook for fetching the organization's CI tokens (metadata only, never the raw token).
 * GET /api/v1/organization/ci-tokens
 *
 * Admin-only on the server (RequireAdmin). Pass enabled:false for non-admins so the
 * query never fires the forbidden request.
 */
export function useCITokens({ enabled = true }: { enabled?: boolean } = {}) {
  const {
    getToken,
    isLoaded,
    isSignedIn,
    orgId: clerkOrganizationId,
  } = useAuth();

  return useQuery<CIToken[]>({
    queryKey: ['ci-tokens', clerkOrganizationId],
    enabled:
      enabled && isLoaded && isSignedIn === true && !!clerkOrganizationId,
    queryFn: async () => {
      const token = await getToken();
      if (!token) {
        throw new Error('No authentication token available');
      }

      const { data, error, response } = await apiClient.GET(
        '/api/v1/organization/ci-tokens',
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (error || !response.ok || !data) {
        throw new Error('Failed to fetch CI tokens');
      }

      return data.ciTokens.map(transformCIToken);
    },
    staleTime: 30000,
  });
}
